import { StandardEvents } from '@shopify/events';

class OvdvServiceZone extends HTMLElement {
  constructor() {
    super();

    this.abortController = new AbortController();
    this.currentVariantId = '';
    this.currentVariantAvailable = false;
    this.validated = false;
    this.form = null;
    this.productForm = null;
    this.eventTarget = null;

    this.handleCheck = this.handleCheck.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleProductSelect = this.handleProductSelect.bind(this);
  }

  connectedCallback() {
    if (this.abortController.signal.aborted) {
      this.abortController = new AbortController();
    }

    this.currentVariantId = String(this.dataset.initialVariantId || '');
    this.currentVariantAvailable =
      this.dataset.initialVariantAvailable === 'true';

    this.form = this.closest('form');
    this.productForm = this.closest('product-form-component');
    this.eventTarget = this.closest('.shopify-section, dialog');

    const { signal } = this.abortController;

    this.checkButton?.addEventListener('click', this.handleCheck, { signal });
    this.countryInput?.addEventListener('change', this.handleInput, { signal });
    this.postcodeInput?.addEventListener('input', this.handleInput, { signal });

    this.form?.addEventListener('submit', this.handleSubmit, {
      capture: true,
      signal,
    });

    this.eventTarget?.addEventListener(
      StandardEvents.productSelect,
      this.handleProductSelect,
      { signal }
    );

    this.applyState();
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  get countryInput() {
    return this.querySelector('[data-service-zone-country]');
  }

  get postcodeInput() {
    return this.querySelector('[data-service-zone-postcode]');
  }

  get validationInput() {
    return this.querySelector('[data-service-zone-validation]');
  }

  get checkButton() {
    return this.querySelector('[data-service-zone-check]');
  }

  get fields() {
    return this.querySelector('[data-service-zone-fields]');
  }

  get status() {
    return this.querySelector('[data-service-zone-status]');
  }

  get contact() {
    return this.querySelector('[data-service-zone-contact]');
  }

  get mainButton() {
    return this.productForm?.querySelector(
      '[data-ovdv-service-zone-button]'
    );
  }

  get acceleratedCheckout() {
    return this.productForm?.querySelector(
      '[data-ovdv-service-zone-accelerated]'
    );
  }

  get stickyButton() {
    const productId = String(this.dataset.productId || '');

    const stickyComponent = Array.from(
      document.querySelectorAll('sticky-add-to-cart')
    ).find((component) => component.dataset.productId === productId);

    return stickyComponent?.querySelector(
      '[data-ovdv-service-zone-sticky-button]'
    );
  }

  get restrictedVariantIds() {
    return new Set(this.parseList(this.dataset.restrictedVariantIds));
  }

  get misconfiguredVariantIds() {
    return new Set(this.parseList(this.dataset.misconfiguredVariantIds));
  }

  get allowedCountries() {
    return new Set(
      this.parseList(this.dataset.allowedCountries)
        .map((country) => this.normalizeCountry(country))
        .filter(Boolean)
    );
  }

  get allowedPostcodes() {
    return new Set(
      this.parseList(this.dataset.allowedPostcodes)
        .map((postcode) => this.normalizePostcode(postcode))
        .filter(Boolean)
    );
  }

  parseList(rawValue) {
    let value = String(rawValue || '').trim();

    if (!value) {
      return [];
    }

    try {
      const parsedValue = JSON.parse(value);

      if (Array.isArray(parsedValue)) {
        return parsedValue
          .map((item) => String(item).trim())
          .filter(Boolean);
      }

      if (typeof parsedValue === 'string') {
        value = parsedValue;
      }
    } catch {
      // Normale Text- oder kommaseparierte Metafields
      // werden unten verarbeitet.
    }

    return value
      .split(/[,;\n|]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  normalizeCountry(value) {
    const normalized = String(value || '')
      .trim()
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    const countryAliases = {
      BE: 'BE',
      BEL: 'BE',
      BELGIUM: 'BE',
      BELGIE: 'BE',
      BELGIEN: 'BE',
      BELGIQUE: 'BE',
    };

    return countryAliases[normalized] || normalized;
  }

  normalizePostcode(value) {
    let normalized = String(value || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '');

    normalized = normalized.replace(
      /^(BE|BEL|BELGIUM|BELGIE|BELGIEN|BELGIQUE)[:\-]?/i,
      ''
    );

    return normalized;
  }

  isRestrictedVariant() {
    return this.restrictedVariantIds.has(this.currentVariantId);
  }

  hasCompleteRules() {
    return (
      this.allowedCountries.size > 0 &&
      this.allowedPostcodes.size > 0
    );
  }

  isMisconfiguredVariant() {
    return (
      this.misconfiguredVariantIds.has(this.currentVariantId) ||
      !this.hasCompleteRules()
    );
  }

  setValidation(value) {
    this.validated = value;

    if (this.validationInput) {
      this.validationInput.value = String(value);
    }
  }

  setInputsEnabled(enabled) {
    if (this.countryInput) {
      this.countryInput.disabled = !enabled;
    }

    if (this.postcodeInput) {
      this.postcodeInput.disabled = !enabled;
    }

    if (this.validationInput) {
      this.validationInput.disabled = !enabled;
    }

    if (this.checkButton) {
      this.checkButton.disabled = !enabled;
    }
  }

  showFields(show) {
    if (this.fields) {
      this.fields.hidden = !show;
    }

    if (this.checkButton) {
      this.checkButton.hidden = !show;
    }
  }

  showContact(show) {
    if (this.contact) {
      this.contact.hidden = !show;
    }
  }

  setStatus(message, type) {
    if (!this.status) {
      return;
    }

    this.status.textContent = message;
    this.status.dataset.status = type;
    this.status.hidden = false;
  }

  clearStatus() {
    if (!this.status) {
      return;
    }

    this.status.textContent = '';
    this.status.removeAttribute('data-status');
    this.status.hidden = true;
  }

  setButtonText(button, text) {
    const textElement = button?.querySelector(
      '[data-ovdv-service-zone-button-text]'
    );

    if (textElement && text) {
      textElement.textContent = text;
    }
  }

  getLockedButtonText(reason) {
    if (reason === 'unavailable') {
      return this.dataset.textUnavailableButton || '';
    }

    if (reason === 'configuration') {
      return this.dataset.textConfigurationButton || '';
    }

    return this.dataset.textLockedButton || '';
  }

  lockPurchase(reason = 'locked') {
    const buttonText = this.getLockedButtonText(reason);

    for (const button of [this.mainButton, this.stickyButton]) {
      if (!button) {
        continue;
      }

      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.dataset.ovdvServiceZoneLocked = 'true';

      this.setButtonText(button, buttonText);
    }

    if (this.acceleratedCheckout) {
      this.acceleratedCheckout.hidden = true;
      this.acceleratedCheckout.dataset.ovdvServiceZoneLocked = 'true';
    }

    this.dataset.state = reason;
  }

  unlockPurchase() {
    if (!this.currentVariantAvailable) {
      return;
    }

    for (const button of [this.mainButton, this.stickyButton]) {
      if (!button) {
        continue;
      }

      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.removeAttribute('data-ovdv-service-zone-locked');

      this.setButtonText(
        button,
        this.dataset.textAddToCart || ''
      );
    }

    if (this.acceleratedCheckout) {
      this.acceleratedCheckout.hidden = false;
      this.acceleratedCheckout.removeAttribute(
        'data-ovdv-service-zone-locked'
      );
    }

    this.dataset.state = 'available';
  }

  clearServiceLock() {
    for (const button of [this.mainButton, this.stickyButton]) {
      if (
        !button ||
        button.dataset.ovdvServiceZoneLocked !== 'true'
      ) {
        continue;
      }

      button.removeAttribute('data-ovdv-service-zone-locked');

      if (this.currentVariantAvailable) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');

        this.setButtonText(
          button,
          this.dataset.textAddToCart || ''
        );
      }
    }

    if (
      this.acceleratedCheckout?.dataset.ovdvServiceZoneLocked ===
      'true'
    ) {
      this.acceleratedCheckout.removeAttribute(
        'data-ovdv-service-zone-locked'
      );

      if (this.currentVariantAvailable) {
        this.acceleratedCheckout.hidden = false;
      }
    }

    this.removeAttribute('data-state');
  }

  applyState() {
    const checkerIsRequired =
      this.currentVariantAvailable &&
      this.isRestrictedVariant();

    if (!checkerIsRequired) {
      this.hidden = true;
      this.setValidation(false);
      this.setInputsEnabled(false);
      this.showFields(false);
      this.showContact(false);
      this.clearStatus();
      this.clearServiceLock();
      return;
    }

    this.hidden = false;
    this.setValidation(false);

    if (this.isMisconfiguredVariant()) {
      this.setInputsEnabled(false);
      this.showFields(false);
      this.showContact(true);

      this.setStatus(
        this.dataset.textConfigurationError || '',
        'error'
      );

      this.lockPurchase('configuration');
      return;
    }

    this.setInputsEnabled(true);
    this.showFields(true);
    this.showContact(false);
    this.clearStatus();
    this.lockPurchase('locked');
  }

  handleInput() {
    if (
      !this.currentVariantAvailable ||
      !this.isRestrictedVariant() ||
      this.isMisconfiguredVariant()
    ) {
      return;
    }

    this.setValidation(false);
    this.clearStatus();
    this.showContact(false);
    this.lockPurchase('locked');
  }

  handleCheck(event) {
    event.preventDefault();

    if (
      !this.currentVariantAvailable ||
      !this.isRestrictedVariant()
    ) {
      return;
    }

    if (this.isMisconfiguredVariant()) {
      this.setValidation(false);

      this.setStatus(
        this.dataset.textConfigurationError || '',
        'error'
      );

      this.showContact(true);
      this.lockPurchase('configuration');
      return;
    }

    const country = this.normalizeCountry(
      this.countryInput?.value
    );

    const postcode = this.normalizePostcode(
      this.postcodeInput?.value
    );

    if (!country || !postcode) {
      this.setValidation(false);

      this.setStatus(
        this.dataset.textIncomplete || '',
        'error'
      );

      this.showContact(false);
      this.lockPurchase('locked');
      return;
    }

    const isAvailable =
      this.allowedCountries.has(country) &&
      this.allowedPostcodes.has(postcode);

    if (isAvailable) {
      this.setValidation(true);

      this.setStatus(
        this.dataset.textAvailable || '',
        'success'
      );

      this.showContact(false);
      this.unlockPurchase();
      return;
    }

    this.setValidation(false);

    this.setStatus(
      this.dataset.textUnavailable || '',
      'error'
    );

    this.showContact(true);
    this.lockPurchase('unavailable');
  }

  handleSubmit(event) {
    if (
      !this.currentVariantAvailable ||
      !this.isRestrictedVariant() ||
      this.validated
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.isMisconfiguredVariant()) {
      this.setStatus(
        this.dataset.textConfigurationError || '',
        'error'
      );

      this.showContact(true);
      this.lockPurchase('configuration');
    } else {
      this.setStatus(
        this.dataset.textIncomplete || '',
        'error'
      );

      this.showContact(false);
      this.lockPurchase('locked');
    }

    this.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }

  handleProductSelect(event) {
    const sourceCard =
      event.target instanceof Element
        ? event.target.closest('product-card')
        : null;

    if (sourceCard) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail) {
          return;
        }

        if (
          detail.productId &&
          String(detail.productId) !==
            String(this.dataset.productId || '')
        ) {
          return;
        }

        this.currentVariantId = String(
          detail.resource?.id || ''
        );

        this.currentVariantAvailable =
          detail.resource?.available === true;

        this.setValidation(false);

        window.requestAnimationFrame(() => {
          this.applyState();
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[OVDV service zone] Variant update failed:',
            error
          );
        }
      });
  }
}

if (!customElements.get('ovdv-service-zone')) {
  customElements.define(
    'ovdv-service-zone',
    OvdvServiceZone
  );
}